/**
 * 移动端漫游控制器 — fly/walk/avatar 模式下显示虚拟摇杆与跳跃触发。
 *
 * 布局：
 * - 左下角虚拟摇杆（前后左右），通过 touch 事件驱动 setJoystickMoveIntent
 * - walk/avatar 模式下屏幕空白区域点击触发 jumpWalk
 *
 * 设计约束：
 * - 组件使用 pointer-events-auto 仅在控制器区域
 * - 不与底部工具栏、量测、热点弹层冲突
 * - avatar 模式下拖拽超过 1.5 倍摇杆半径触发奔跑
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useViewerStore } from '@stores/viewerStore';
import type { RendererController } from '@renderer/index';

interface MobileGameControllerProps {
  getController: () => RendererController | null;
}

// 摇杆物理尺寸
const JOYSTICK_SIZE = 140;
const KNOB_SIZE = 56;
const MAX_OFFSET = (JOYSTICK_SIZE - KNOB_SIZE) / 2;

// 拖拽超过 1.5 倍半径视为奔跑（avatar 模式）
const SPRINT_DRAG_RATIO = 1.5;

// 高度摇杆尺寸（竖直细条）
const ALT_JOYSTICK_WIDTH = 48;
const ALT_JOYSTICK_HEIGHT = 140;
const ALT_KNOB_SIZE = 44;
const ALT_MAX_OFFSET = (ALT_JOYSTICK_HEIGHT - ALT_KNOB_SIZE) / 2;

export function MobileGameController({ getController }: MobileGameControllerProps) {
  const joystickRef = useRef<HTMLDivElement>(null);
  const activeTouchRef = useRef<number | null>(null);
  const [knobOffset, setKnobOffset] = useState({ x: 0, y: 0 });
  const rafRef = useRef<number>(0);
  const sprintRef = useRef(false);
  const navMode = useViewerStore((s) => s.navigationMode);
  const isFly = navMode === 'fly';

  // 持续发送移动意图（摇杆松开前保持发送）
  const intentRef = useRef({ x: 0, y: 0 });
  const altitudeRef = useRef(0);

  const sendIntent = useCallback(() => {
    const ctrl = getController();
    if (ctrl) {
      ctrl.navigation.walk.setJoystickIntent({
        ...intentRef.current,
        sprint: sprintRef.current,
        altitude: altitudeRef.current,
      });
    }
  }, [getController]);

  // --- 高度摇杆状态 ---
  const altJoystickRef = useRef<HTMLDivElement>(null);
  const altActiveTouchRef = useRef<number | null>(null);
  const [altKnobY, setAltKnobY] = useState(0);

  const updateAltKnob = useCallback(
    (touch: React.Touch) => {
      const el = altJoystickRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cy = rect.top + rect.height / 2;
      let dy = touch.clientY - cy;
      if (Math.abs(dy) > ALT_MAX_OFFSET) {
        dy = dy > 0 ? ALT_MAX_OFFSET : -ALT_MAX_OFFSET;
      }
      setAltKnobY(dy);
      // 向上 dy<0 → altitude>0 → 上升
      altitudeRef.current = -dy / ALT_MAX_OFFSET;
      sendIntent();
    },
    [sendIntent],
  );

  const altTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (altActiveTouchRef.current !== null) return;
      altActiveTouchRef.current = e.changedTouches[0].identifier;
      updateAltKnob(e.changedTouches[0]);
      e.stopPropagation();
    },
    [updateAltKnob],
  );

  const altTouchMove = useCallback(
    (e: React.TouchEvent) => {
      for (let i = 0; i < e.changedTouches.length; i++) {
        if (e.changedTouches[i].identifier === altActiveTouchRef.current) {
          updateAltKnob(e.changedTouches[i]);
          break;
        }
      }
    },
    [updateAltKnob],
  );

  const altTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      for (let i = 0; i < e.changedTouches.length; i++) {
        if (e.changedTouches[i].identifier === altActiveTouchRef.current) {
          altActiveTouchRef.current = null;
          setAltKnobY(0);
          altitudeRef.current = 0;
          sendIntent();
          break;
        }
      }
    },
    [sendIntent],
  );

  const updateKnob = useCallback(
    (touch: React.Touch) => {
      const el = joystickRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      let dx = touch.clientX - cx;
      let dy = touch.clientY - cy;
      const rawDist = Math.sqrt(dx * dx + dy * dy);
      if (rawDist > MAX_OFFSET) {
        dx = (dx / rawDist) * MAX_OFFSET;
        dy = (dy / rawDist) * MAX_OFFSET;
      }
      setKnobOffset({ x: dx, y: dy });
      // 归一化为 joystick 输入：x=右, y 与屏幕偏移一致（向上 dy<0 → y<0 → viewer 内 -y > 0 → 前进）
      intentRef.current = {
        x: dx / MAX_OFFSET,
        y: dy / MAX_OFFSET,
      };
      // 拖拽距离超过 1.5 倍摇杆半径 → 奔跑
      sprintRef.current = rawDist > MAX_OFFSET * SPRINT_DRAG_RATIO;
      sendIntent();
    },
    [sendIntent],
  );

  // 摇杆 touch 逻辑
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (activeTouchRef.current !== null) return;
      const touch = e.changedTouches[0];
      activeTouchRef.current = touch.identifier;
      updateKnob(touch);
      e.stopPropagation();
    },
    [updateKnob],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      for (let i = 0; i < e.changedTouches.length; i++) {
        if (e.changedTouches[i].identifier === activeTouchRef.current) {
          updateKnob(e.changedTouches[i]);
          break;
        }
      }
    },
    [updateKnob],
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      for (let i = 0; i < e.changedTouches.length; i++) {
        if (e.changedTouches[i].identifier === activeTouchRef.current) {
          activeTouchRef.current = null;
          setKnobOffset({ x: 0, y: 0 });
          intentRef.current = { x: 0, y: 0 };
          sprintRef.current = false;
          // 不重置 altitudeRef — 高度摇杆可能仍在操作中
          sendIntent();
          break;
        }
      }
    },
    [sendIntent],
  );

  // 动画帧持续发送（保证持续按住时 intent 不断更新）
  useEffect(() => {
    let running = true;
    const loop = () => {
      if (!running) return;
      if (activeTouchRef.current !== null) {
        sendIntent();
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      running = false;
      cancelAnimationFrame(rafRef.current);
    };
  }, [sendIntent]);

  return (
    <>
      {/* 跳跃由 viewer 内部 touch.ts tap 检测触发，不需要覆盖层 */}

      {/* 虚拟摇杆 — 左下角，避开底部工具栏 */}
      <div
        ref={joystickRef}
        data-walk-joystick
        className="pointer-events-auto absolute bottom-[100px] left-5 select-none"
        style={{
          width: JOYSTICK_SIZE,
          height: JOYSTICK_SIZE,
          touchAction: 'none',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        {/* 底盘 */}
        <div className="absolute inset-0 rounded-full border-2 border-white/30 bg-black/20 backdrop-blur-sm" />

        {/* 方向指示十字 */}
        <div className="absolute inset-0 flex items-center justify-center">
          <svg width="60" height="60" viewBox="0 0 60 60" className="opacity-30">
            {/* 上 */}
            <path d="M30 8 L26 16 L34 16 Z" fill="white" />
            {/* 下 */}
            <path d="M30 52 L26 44 L34 44 Z" fill="white" />
            {/* 左 */}
            <path d="M8 30 L16 26 L16 34 Z" fill="white" />
            {/* 右 */}
            <path d="M52 30 L44 26 L44 34 Z" fill="white" />
          </svg>
        </div>

        {/* 摇杆旋钮 */}
        <div
          className="absolute rounded-full border-2 border-white/60 bg-white/40 shadow-lg backdrop-blur-md transition-[width,height] duration-75"
          style={{
            width: KNOB_SIZE,
            height: KNOB_SIZE,
            left: '50%',
            top: '50%',
            transform: `translate(calc(-50% + ${knobOffset.x}px), calc(-50% + ${knobOffset.y}px))`,
          }}
        />
      </div>

      {/* 高度升降摇杆 — 仅 fly 模式，右下角竖直条 */}
      {isFly && (
        <div
          ref={altJoystickRef}
          data-walk-joystick
          className="pointer-events-auto absolute right-5 bottom-[100px] select-none"
          style={{
            width: ALT_JOYSTICK_WIDTH,
            height: ALT_JOYSTICK_HEIGHT,
            touchAction: 'none',
          }}
          onTouchStart={altTouchStart}
          onTouchMove={altTouchMove}
          onTouchEnd={altTouchEnd}
          onTouchCancel={altTouchEnd}
        >
          {/* 底盘 */}
          <div className="absolute inset-0 rounded-full border-2 border-white/30 bg-black/20 backdrop-blur-sm" />

          {/* 上下箭头提示 */}
          <div className="absolute inset-0 flex flex-col items-center justify-between py-2">
            <svg width="20" height="12" viewBox="0 0 20 12" className="opacity-30">
              <path d="M10 1 L4 9 L16 9 Z" fill="white" />
            </svg>
            <svg width="20" height="12" viewBox="0 0 20 12" className="opacity-30">
              <path d="M10 11 L4 3 L16 3 Z" fill="white" />
            </svg>
          </div>

          {/* 旋钮 */}
          <div
            className="absolute rounded-full border-2 border-white/60 bg-white/40 shadow-lg backdrop-blur-md"
            style={{
              width: ALT_KNOB_SIZE,
              height: ALT_KNOB_SIZE,
              left: '50%',
              top: '50%',
              transform: `translate(-50%, calc(-50% + ${altKnobY}px))`,
            }}
          />
        </div>
      )}
    </>
  );
}

/** @deprecated 使用 MobileGameController */
export const MobileWalkController = MobileGameController;
